import { useState, type Dispatch, type SetStateAction } from "react";
import { useI18N } from "@src/language/language-context";
import { cancelInstallationProcessUseCase } from "@src/cloud-connect/ota-update/use-cases/cancel-installation-process-use-case";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import type { InstallAndActionBlock } from "@src/cloud-connect/ota-update/constants";
import { ActiveInstallationProcessPayload } from "@src/types/ota-update";
import { OTA_OBJECT_TYPE } from "@src/cloud-connect/ota-update/constants";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";

interface UseOtaPendingApprovalByOperatorProps {
  activeInstallationData: ActiveInstallationProcessPayload | null;
  setInstallAndActionBlock: Dispatch<
    SetStateAction<InstallAndActionBlock | null>
  >;
}

export const useOtaPendingApprovalByOperator = ({
  activeInstallationData,
  setInstallAndActionBlock,
}: UseOtaPendingApprovalByOperatorProps) => {
  const i18n = useI18N();
  const [isCancelRequestLoading, setIsCancelRequestLoading] = useState(false);

  const isBackupInstallation =
    activeInstallationData?.firmware?.type === OTA_OBJECT_TYPE.FIRMWARE_BACKUP;
  const installationName = activeInstallationData?.firmware?.filename || "";

  const titleTemplate = isBackupInstallation
    ? i18n("modules.otaPendingApprovalByOperator.titleTemplateBackup")
    : i18n("modules.otaPendingApprovalByOperator.titleTemplateFirmware");

  const title = titleTemplate.replace("{itemName}", installationName);

  const itemNameMarker = "__ITEM_NAME__";
  const titleTemplateWithMarker = titleTemplate.replace(
    "{itemName}",
    itemNameMarker,
  );
  const [titlePrefix, titleSuffix = ""] =
    titleTemplateWithMarker.split(itemNameMarker);

  const texts = {
    title,
    titleHasItemName: titleTemplate.includes("{itemName}"),
    titlePrefix,
    titleSuffix,
    itemName: installationName,
    description: isBackupInstallation
      ? i18n("modules.otaPendingApprovalByOperator.descriptionBackup")
      : i18n("modules.otaPendingApprovalByOperator.descriptionFirmware"),
    cancelRequestButtonText: i18n(
      "modules.otaPendingApprovalByOperator.cancelRequestButtonText",
    ),
    cancelSuccessMessage: i18n(
      "modules.otaPendingApprovalByOperator.cancelSuccessMessage",
    ),
    cancelFailureMessage: i18n(
      "modules.otaPendingApprovalByOperator.cancelFailureMessage",
    ),
  };

  const isDebugLoggingEnabled = useFeatureFlag("allow-debug", false);

  const handleCancelRequest = async () => {
    if (isCancelRequestLoading) {
      return;
    }

    const updateId = activeInstallationData?.update_id;
    if (!updateId) {
      return;
    }

    setIsCancelRequestLoading(true);

    try {
      const response = await cancelInstallationProcessUseCase(updateId);
      isDebugLoggingEnabled &&
        console.log(
          "OTA_DEBUG: Cancel installation process response:",
          response,
          "for updateId:",
          updateId,
        );
      if (response.success) {
        setInstallAndActionBlock(null);
        successSnackbar(texts.cancelSuccessMessage, 5000);
        return;
      }

      errorSnackbar(texts.cancelFailureMessage, 5000);
      setIsCancelRequestLoading(false);
    } catch {
      errorSnackbar(texts.cancelFailureMessage, 5000);
      setIsCancelRequestLoading(false);
    }
  };

  return {
    texts,
    handleCancelRequest,
    isCancelRequestLoading,
  };
};
